import React, { useState, useMemo, useEffect } from 'react';
import { database } from '../firebase';
import { ref as dbRef, get, set } from 'firebase/database';

const AdminAppointmentBooking = () => {
  // State for appointments
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch appointments from Firebase
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const appointmentsRef = dbRef(database, 'Dr/appointments');
        const snapshot = await get(appointmentsRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          const appointmentsArray = [];
          
          // Transform nested structure to flat array
          // Structure: Dr/appointments/{phone}/{index}
          Object.keys(data).forEach(phone => {
            const phoneAppointments = data[phone];
            Object.keys(phoneAppointments).forEach(index => {
              const appointment = phoneAppointments[index];
              appointmentsArray.push({
                id: `${phone}_${index}`, // Unique ID
                phone: phone,
                appointmentIndex: index,
                fullName: appointment.name,
                email: appointment.email || '',
                date: appointment.date,
                disease: appointment.department,
                symptoms: appointment.symptoms || '',
                status: appointment.status || 'Pending',
                createdAt: appointment.timestamp 
                  ? new Date(appointment.timestamp).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'N/A'
              });
            });
          });
          
          // Sort by timestamp (newest first)
          appointmentsArray.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB - dateA;
          });
          
          setAppointments(appointmentsArray);
        } else {
          setAppointments([]);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  // State for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedDate, setSelectedDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Filter appointments based on search and filters
  const filteredAppointments = useMemo(() => {
    let filtered = appointments;

    // Search by name or phone
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(appointment =>
        appointment.fullName.toLowerCase().includes(query) ||
        appointment.phone.toLowerCase().includes(query) ||
        appointment.email.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(appointment => appointment.status === statusFilter);
    }

    // Filter by date
    if (selectedDate) {
      filtered = filtered.filter(appointment => appointment.date === selectedDate);
    }

    return filtered;
  }, [appointments, searchQuery, statusFilter, selectedDate]);

  // Get appointments grouped by date
  const getAppointmentsByDate = () => {
    const grouped = {};
    
    filteredAppointments.forEach(appointment => {
      if (!grouped[appointment.date]) {
        grouped[appointment.date] = [];
      }
      grouped[appointment.date].push(appointment);
    });
    
    // Sort dates in ascending order
    const sortedDates = Object.keys(grouped).sort();
    
    // Create sorted object
    const sortedGrouped = {};
    sortedDates.forEach(date => {
      sortedGrouped[date] = grouped[date];
    });
    
    return sortedGrouped;
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Get days in month for calendar
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Previous month's days
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: `${year}-${String(month).padStart(2, '0')}-${String(prevMonthDays - i).padStart(2, '0')}`
      });
    }
    
    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasAppointment = appointments.some(app => app.date === dateStr);
      
      days.push({
        day,
        isCurrentMonth: true,
        date: dateStr,
        hasAppointment
      });
    }
    
    // Next month's days
    const totalCells = 42; // 6 weeks
    const nextMonthDays = totalCells - days.length;
    for (let day = 1; day <= nextMonthDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: `${year}-${String(month + 2).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }
    
    return days;
  };

  // Delete an appointment
  const handleDeleteAppointment = async (id) => {
    try {
      // Parse the ID to get phone and index
      const [phone, index] = id.split('_');
      const appointmentRef = dbRef(database, `Dr/appointments/${phone}/${index}`);
      
      // Delete from Firebase
      await set(appointmentRef, null);
      
      // Refresh appointments
      const appointmentsRef = dbRef(database, 'Dr/appointments');
      const snapshot = await get(appointmentsRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const appointmentsArray = [];
        
        Object.keys(data).forEach(phone => {
          const phoneAppointments = data[phone];
          Object.keys(phoneAppointments).forEach(index => {
            const appointment = phoneAppointments[index];
            appointmentsArray.push({
              id: `${phone}_${index}`,
              phone: phone,
              appointmentIndex: index,
              fullName: appointment.name,
              email: appointment.email || '',
              date: appointment.date,
              disease: appointment.department,
              symptoms: appointment.symptoms || '',
              status: appointment.status || 'Pending',
              createdAt: appointment.timestamp 
                ? new Date(appointment.timestamp).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'N/A'
            });
          });
        });
        
        setAppointments(appointmentsArray);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment');
    }
  };

  // Update appointment status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      // Parse the ID to get phone and index
      const [phone, index] = id.split('_');
      const appointmentRef = dbRef(database, `Dr/appointments/${phone}/${index}/status`);
      
      // Update status in Firebase
      await set(appointmentRef, newStatus);
      
      // Update local state
      setAppointments(appointments.map(appointment => 
        appointment.id === id 
          ? { ...appointment, status: newStatus }
          : appointment
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update appointment status');
    }
  };

  const appointmentsByDate = getAppointmentsByDate();
  const calendarDays = generateCalendarDays();
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-xl text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xl text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Appointment Management</h1>
              <p className="text-gray-600 mt-2">Manage and view all patient appointments</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-teal-600 rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative px-4 py-2 bg-white ring-1 ring-gray-900/5 rounded-lg leading-none flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-800 font-medium">{appointments.length} Total Appointments</span>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Search Bar */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by patient name, phone number, or email..."
                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-2 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status Filter
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  >
                    <option value="All">All Status</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Pending">Pending</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Date Filter */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Filter
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={selectedDate}
                      onClick={() => setShowCalendar(!showCalendar)}
                      placeholder="Select date..."
                      readOnly
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition cursor-pointer"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  {/* Calendar Popup */}
                  {showCalendar && (
                    <div className="absolute z-50 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 p-4">
                      <div className="flex justify-between items-center mb-4">
                        <button
                          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <h3 className="font-semibold text-gray-800">
                          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                        </h3>
                        <button
                          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>

                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map(day => (
                          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                            {day}
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((dayData, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              if (dayData.isCurrentMonth) {
                                setSelectedDate(dayData.date);
                                setShowCalendar(false);
                              }
                            }}
                            className={`h-8 rounded-lg text-sm transition-all ${
                              !dayData.isCurrentMonth
                                ? 'text-gray-300'
                                : dayData.date === selectedDate
                                ? 'bg-blue-600 text-white font-bold'
                                : dayData.hasAppointment
                                ? 'bg-blue-50 text-blue-700 font-medium hover:bg-blue-100'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {dayData.day}
                          </button>
                        ))}
                      </div>

                      <div className="mt-4 pt-4 border-t flex justify-between">
                        <button
                          onClick={() => {
                            setSelectedDate('');
                            setShowCalendar(false);
                          }}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Clear Filter
                        </button>
                        <button
                          onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Today
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Clear Filters Button */}
            {(searchQuery || statusFilter !== 'All' || selectedDate) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('All');
                    setSelectedDate('');
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium mb-1">Total Appointments</p>
                  <p className="text-3xl font-bold text-gray-800">{filteredAppointments.length}</p>
                  <p className="text-xs text-gray-500 mt-1">{appointments.length} total in system</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl shadow-lg p-6 border border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium mb-1">Confirmed</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {filteredAppointments.filter(a => a.status === 'Confirmed').length}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((filteredAppointments.filter(a => a.status === 'Confirmed').length / (filteredAppointments.length || 1)) * 100)}% of current
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-yellow-50 rounded-2xl shadow-lg p-6 border border-yellow-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 font-medium mb-1">Pending</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {filteredAppointments.filter(a => a.status === 'Pending').length}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Requires attention</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-xl">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl shadow-lg p-6 border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium mb-1">Unique Patients</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {new Set(filteredAppointments.map(a => a.email)).size}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Active patients</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-xl">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Appointments Table by Date */}
        {filteredAppointments.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-3">No appointments found</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              {searchQuery || statusFilter !== 'All' || selectedDate
                ? "Try adjusting your search or filters to find what you're looking for."
                : "There are no upcoming appointments scheduled at the moment."}
            </p>
            {(searchQuery || statusFilter !== 'All' || selectedDate) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('All');
                  setSelectedDate('');
                }}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition"
              >
                Clear filters and show all appointments
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(appointmentsByDate).map(([date, dateAppointments]) => (
              <div key={date} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Date Header */}
                <div className="bg-gradient-to-r from-blue-600 to-teal-600 px-8 py-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{formatDate(date)}</h2>
                      <div className="flex items-center mt-2">
                        <div className="flex items-center text-blue-100 mr-4">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>{dateAppointments.length} appointment{dateAppointments.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="hidden md:flex items-center space-x-2">
                          {dateAppointments.filter(a => a.status === 'Confirmed').length > 0 && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              {dateAppointments.filter(a => a.status === 'Confirmed').length} Confirmed
                            </span>
                          )}
                          {dateAppointments.filter(a => a.status === 'Pending').length > 0 && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                              {dateAppointments.filter(a => a.status === 'Pending').length} Pending
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 md:mt-0 flex items-center space-x-3">
                      <span className="px-4 py-2 bg-white/20 text-white text-sm font-medium rounded-full backdrop-blur-sm">
                        {date}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Table for this date */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Patient Details
                        </th>
                        <th scope="col" className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Contact Information
                        </th>
                        <th scope="col" className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Medical Information
                        </th>
                        <th scope="col" className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dateAppointments.map((appointment) => (
                        <tr key={appointment.id} className="hover:bg-gray-50 transition-colors">
                          {/* Patient Details */}
                          <td className="px-8 py-5">
                            <div>
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                  <span className="text-blue-600 font-semibold">
                                    {appointment.fullName.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900">{appointment.fullName}</div>
                                  <div className="text-sm text-gray-500">
                                    Booked: {appointment.createdAt}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          {/* Contact Info */}
                          <td className="px-8 py-5">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span className="font-medium text-gray-900">{appointment.phone}</span>
                              </div>
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span className="text-gray-600 truncate max-w-xs">{appointment.email}</span>
                              </div>
                            </div>
                          </td>
                          
                          {/* Disease & Symptoms */}
                          <td className="px-8 py-5">
                            <div>
                              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 mb-2">
                                {appointment.disease}
                              </div>
                              <div className="text-sm text-gray-600">
                                {appointment.symptoms || 'No symptoms reported'}
                              </div>
                            </div>
                          </td>
                          
                          {/* Status */}
                          <td className="px-8 py-5 whitespace-nowrap">
                            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                              appointment.status === 'Confirmed' 
                                ? 'bg-green-100 text-green-800' 
                                : appointment.status === 'Cancelled' 
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {appointment.status}
                            </span>
                          </td>
                          
                          {/* Actions */}
                          <td className="px-8 py-5 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => handleUpdateStatus(appointment.id, 'Confirmed')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                                title="Confirm Appointment"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(appointment.id, 'Pending')}
                                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition"
                                title="Mark as Pending"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(appointment.id, 'Cancelled')}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Cancel Appointment"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteAppointment(appointment.id)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                title="Delete Appointment"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAppointmentBooking;